import React, { useState, useContext, useCallback, useMemo } from 'react';
import FilterIcon from 'mdi-react/FilterVariantIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import RouteContext from '../../../../routes/context';
import {
  FilterSlice,
  ProductCollectionFilter,
  ProductCollectionNameSearchFilter,
} from '../../../../types';
import { useViewportContext } from '../../../../components/viewport/context';
import { trackProductCollectionFilterClear } from '../../tracking';
import SidebarFilter from './sidebar-filter';
import OverlayFilter from './overlay-filter';
import FeaturedFilters from '../featured-filters';
import * as S from './styles';
import { ProductCollectionNonNameSearchFilter } from './types';

type Props = Pick<FilterSlice, 'id' | 'sections' | 'filters' | 'productsCount'>;

const isNameSearchFilter = (
  filter: ProductCollectionFilter
): filter is ProductCollectionNameSearchFilter =>
  'type' in filter && filter.type === 'name-search';

const Filter = ({ sections, filters, productsCount }: Props) => {
  const { t } = useContext(AppContext);
  const { dispatch, getState } = useContext(RouteContext);
  const { isMobile, isTablet } = useViewportContext();
  const [mobileFilterOpen, setMobileOpen] = useState(false);
  const mobileFilterIcon = useMemo(() => <FilterIcon size={18} />, []);
  const toggleMobileFilter = useCallback(() => {
    setMobileOpen(!mobileFilterOpen);
  }, [mobileFilterOpen]);
  const featuredFilterSections = useMemo(
    () => sections.filter(item => item.featured),
    [sections]
  );
  const mainFiltersTEMpNAME = useMemo(
    () =>
      filters.filter(
        (filter): filter is ProductCollectionNonNameSearchFilter =>
          !isNameSearchFilter(filter)
      ),
    [filters]
  );
  const clearFilter = useCallback(
    (property?: string) => {
      trackProductCollectionFilterClear(getState().filters, property);
      dispatch({ type: 'FILTER_REMOVE', property });
    },
    [dispatch, getState]
  );

  return sections.length === 0 ? null : (
    <S.Wrapper>
      <FeaturedFilters
        filters={mainFiltersTEMpNAME}
        sections={featuredFilterSections}
        clearFilter={clearFilter}
      />

      <S.MobileFilterToggle
        color="primary"
        onClick={toggleMobileFilter}
        icon={mobileFilterIcon}
      >
        {t('filter', 'filter.title')}
        {filters.length > 0 && (
          <S.AppliedFilterCount inverted>{filters.length}</S.AppliedFilterCount>
        )}
      </S.MobileFilterToggle>

      {(isMobile || isTablet) && mobileFilterOpen && (
        <OverlayFilter
          productsCount={productsCount}
          setMobileOpen={setMobileOpen}
          sections={sections}
          filters={mainFiltersTEMpNAME}
          clearFilter={clearFilter}
        />
      )}

      <SidebarFilter
        key="sidebar"
        sections={sections}
        filters={mainFiltersTEMpNAME}
      />
    </S.Wrapper>
  );
};

export default Filter;
